import { unzipSync, type Zippable, zipSync } from 'fflate';

import type {
  Aircraft,
  AltitudeRef,
  Flight,
  LogbookCheck,
  LogbookEntry,
  Setting,
  Tombstone,
  TombstoneTable,
  Track,
  TrackFile,
  TrackPoints,
} from '@/lib/db';
import {
  aircraft as aircraftRepo,
  db,
  decodeTrackFile,
  encodeTrackFile,
  flights as flightsRepo,
  isPortableSetting,
  logbookChecks as logbookChecksRepo,
  logbookEntries as logbookEntriesRepo,
  SCHEMA_VERSION,
  settings as settingsRepo,
  tombstones as tombstonesRepo,
  trackFiles as trackFilesRepo,
  tracks as tracksRepo,
  upgradeAircraftCategory,
} from '@/lib/db';
import { exportTrack } from '@/lib/track-export';

import { decodePoints, encodePoints, type PointsHeader } from './points-codec';

/**
 * The `.fdv` archive — `PLAN.md` §6 Phase 4 and `ARCHITECTURE.md` §7:
 * "export/import a single `.fdv` archive (zipped JSON + tracks)". Since
 * Phase 7 it is also the unit of sync: each device writes its own `.fdv` into
 * a shared folder and merges everyone else's (`features/sync`,
 * `docs/SYNC.md`). One format, so a backup and a sync file can each be read
 * as the other.
 *
 * A zip with up to four parts:
 *
 *  - `manifest.json`, which says what this is, who wrote it and how much of
 *    it there is;
 *  - `db.json`, every table's rows and the deletions this device remembers;
 *  - `points/<track id>.bin`, each track's points as bytes (`points-codec.ts`);
 *  - `files/<file id>/<original filename>`, each imported file byte for byte —
 *    in a backup always, in a sync file only when the pilot asks for them.
 *
 * The originals sit in the zip as themselves rather than base64 inside the
 * JSON, so a backup unzipped on a computer with no copy of this app still
 * hands back every KML and CSV a pilot ever imported, under the names they
 * had. That is also the recovery path `ARCHITECTURE.md` promised: a parser
 * bug fixed later is fixed for old flights by re-importing these.
 *
 * Version 1 archives carried points as JSON arrays inside `db.json` and no
 * deletions. They still restore exactly as they always did.
 */

export const BACKUP_FORMAT = 'flight-data-viewer-backup';
export const BACKUP_FORMAT_VERSION = 2;

/** Version 1's points: JSON arrays inside `db.json`. Read, never written. */
interface SerialisedTrackPoints {
  trackId: string;
  t: number[];
  lat: number[];
  lon: number[];
  altFt: number[];
  gsKt: number[];
  trackDeg: number[];
  vsFpm?: number[];
  altitudeRef: AltitudeRef;
}

/** The device that wrote a sync file. Absent from a backup taken by hand. */
export interface ArchiveDevice {
  id: string;
  name: string;
}

export interface BackupManifest {
  format: typeof BACKUP_FORMAT;
  version: number;
  exportedAt: string;
  schemaVersion: number;
  device?: ArchiveDevice;
  counts: {
    tracks: number;
    flights: number;
    aircraft: number;
    logbookEntries: number;
    logbookChecks: number;
    settings: number;
    /** Optional: absent from a backup taken before originals were kept. */
    trackFiles?: number;
    /** Optional: absent before version 2. */
    tombstones?: number;
  };
}

/** A `TrackFile` as `db.json` carries it: everything but the bytes, which are their own zip entry. */
type SerialisedTrackFile = Omit<TrackFile, 'data' | 'encoding'>;

function trackFilePath(file: Pick<TrackFile, 'id' | 'filename'>): string {
  // The filename is the pilot's and could hold anything; only the last path
  // segment survives, so a name with slashes in it cannot climb out of its
  // own folder when the zip is extracted.
  const name = file.filename.split(/[\\/]/).pop() ?? '';
  return `files/${file.id}/${name === '' ? 'file' : name}`;
}

function pointsPath(trackId: string): string {
  return `points/${trackId}.bin`;
}

interface BackupData {
  tracks: Track[];
  /** Version 1 only. */
  trackPoints?: SerialisedTrackPoints[];
  /** Version 2: one header per `points/<track id>.bin`. */
  points?: PointsHeader[];
  flights: Flight[];
  aircraft: Aircraft[];
  /** Optional: a backup taken before Phase 6 never wrote this key. */
  logbookEntries?: LogbookEntry[];
  /** Optional: a backup taken before the CAA 1373 redesign never wrote this key. */
  logbookChecks?: LogbookCheck[];
  settings: Setting[];
  /** Optional: a backup taken before originals were kept never wrote this key. */
  trackFiles?: SerialisedTrackFile[];
  /** Optional: absent before version 2. */
  tombstones?: Tombstone[];
}

function deserialisePoints(points: SerialisedTrackPoints): TrackPoints {
  return {
    trackId: points.trackId,
    t: Float64Array.from(points.t),
    lat: Float64Array.from(points.lat),
    lon: Float64Array.from(points.lon),
    altFt: Float32Array.from(points.altFt),
    gsKt: Float32Array.from(points.gsKt),
    trackDeg: Float32Array.from(points.trackDeg),
    ...(points.vsFpm === undefined ? {} : { vsFpm: Float32Array.from(points.vsFpm) }),
    altitudeRef: points.altitudeRef,
  };
}

export interface BuildArchiveOptions {
  /**
   * The original imported files. Always in a backup. Off by default in a
   * sync file, which is rewritten after every change: an FR24 KML is several
   * megabytes, and the points already carry everything the app shows.
   */
  includeOriginals: boolean;
  /** Written into the manifest of a sync file. */
  device?: ArchiveDevice;
}

/** The archive's bytes. `buildBackupArchive` wraps this for a download. */
export async function buildArchive(options: BuildArchiveOptions): Promise<Uint8Array<ArrayBuffer>> {
  const [
    tracks,
    trackPoints,
    flights,
    aircraft,
    logbookEntries,
    logbookChecks,
    settingRows,
    trackFiles,
    tombstones,
  ] = await Promise.all([
    db.tracks.toArray(),
    db.trackPoints.toArray(),
    db.flights.toArray(),
    db.aircraft.toArray(),
    db.logbookEntries.toArray(),
    db.logbookChecks.toArray(),
    settingsRepo.listRows(),
    options.includeOriginals ? trackFilesRepo.list() : Promise.resolve([]),
    tombstonesRepo.list(),
  ]);
  const settings = settingRows.filter((row) => isPortableSetting(row.key));

  const entries: Zippable = {};
  const pointHeaders: PointsHeader[] = [];
  for (const points of trackPoints) {
    const { header, bytes } = encodePoints(points);
    pointHeaders.push(header);
    // Float bytes gain little from hard compression and cost the most time
    // to compress; the fast setting takes most of what there is.
    entries[pointsPath(points.trackId)] = [bytes, { level: 1 }];
  }

  const data: BackupData = {
    tracks,
    points: pointHeaders,
    flights,
    aircraft,
    logbookEntries,
    logbookChecks,
    settings,
    trackFiles: trackFiles.map(({ data: _data, encoding: _encoding, ...rest }) => rest),
    tombstones,
  };
  const manifest: BackupManifest = {
    format: BACKUP_FORMAT,
    version: BACKUP_FORMAT_VERSION,
    exportedAt: new Date().toISOString(),
    schemaVersion: SCHEMA_VERSION,
    ...(options.device === undefined ? {} : { device: options.device }),
    counts: {
      tracks: tracks.length,
      flights: flights.length,
      aircraft: aircraft.length,
      logbookEntries: logbookEntries.length,
      logbookChecks: logbookChecks.length,
      settings: settings.length,
      trackFiles: trackFiles.length,
      tombstones: tombstones.length,
    },
  };

  entries['manifest.json'] = new TextEncoder().encode(JSON.stringify(manifest, null, 2));
  entries['db.json'] = new TextEncoder().encode(JSON.stringify(data));
  for (const file of trackFiles) {
    entries[trackFilePath(file)] = decodeTrackFile(file);
  }

  // Track points and JSON compress well enough at the middle setting; a
  // backup is written rarely, and a sync file is written after the pilot has
  // stopped typing, so it is worth spending some CPU to keep it small.
  return zipSync(entries, { level: 6 });
}

/** A backup to download: every table and every original file. */
export async function buildBackupArchive(): Promise<Blob> {
  const bytes = await buildArchive({ includeOriginals: true });
  return new Blob([bytes], { type: 'application/zip' });
}

export class BackupArchiveError extends Error {}

export interface RestoreSummary {
  tracksAdded: number;
  tracksSkipped: number;
  aircraftAdded: number;
  aircraftSkipped: number;
  flightsAdded: number;
  flightsSkipped: number;
  logbookEntriesAdded: number;
  logbookEntriesSkipped: number;
  logbookChecksAdded: number;
  logbookChecksSkipped: number;
  settingsAdded: number;
  settingsSkipped: number;
  trackFilesAdded: number;
  trackFilesSkipped: number;
  /** Records already here that the archive held a newer copy of. */
  recordsUpdated: number;
  /** Records deleted because the archive's device deleted them later than they were last edited here. */
  recordsDeleted: number;
}

function emptySummary(): RestoreSummary {
  return {
    tracksAdded: 0,
    tracksSkipped: 0,
    aircraftAdded: 0,
    aircraftSkipped: 0,
    flightsAdded: 0,
    flightsSkipped: 0,
    logbookEntriesAdded: 0,
    logbookEntriesSkipped: 0,
    logbookChecksAdded: 0,
    logbookChecksSkipped: 0,
    settingsAdded: 0,
    settingsSkipped: 0,
    trackFilesAdded: 0,
    trackFilesSkipped: 0,
    recordsUpdated: 0,
    recordsDeleted: 0,
  };
}

interface OpenedArchive {
  manifest: BackupManifest;
  data: BackupData;
  /** Decompresses only the entries asked for — a sync file's points are most of its size. */
  read: (paths: ReadonlySet<string>) => Record<string, Uint8Array>;
}

function openArchive(bytes: Uint8Array): OpenedArchive {
  let head: Record<string, Uint8Array>;
  try {
    head = unzipSync(bytes, {
      filter: (file) => file.name === 'manifest.json' || file.name === 'db.json',
    });
  } catch (cause) {
    throw new BackupArchiveError(
      'This file could not be unzipped — it may not be a Flight Data Viewer backup.',
      { cause },
    );
  }

  const manifestBytes = head['manifest.json'];
  const dataBytes = head['db.json'];
  if (manifestBytes === undefined || dataBytes === undefined) {
    throw new BackupArchiveError('This archive is missing manifest.json or db.json.');
  }

  let manifestRaw: unknown;
  let data: BackupData;
  try {
    manifestRaw = JSON.parse(new TextDecoder().decode(manifestBytes));
    data = JSON.parse(new TextDecoder().decode(dataBytes)) as BackupData;
  } catch (cause) {
    throw new BackupArchiveError('This archive is corrupt — its JSON could not be read.', {
      cause,
    });
  }

  // `manifestRaw` is untrusted file content, not something the type system
  // ever vouched for — checked here as a plain unknown rather than asserted
  // straight into `BackupManifest`, so a file that merely *claims* to be one
  // gets refused rather than crashing deeper in restore.
  const format =
    typeof manifestRaw === 'object' && manifestRaw !== null && 'format' in manifestRaw
      ? manifestRaw.format
      : undefined;
  if (format !== BACKUP_FORMAT) {
    throw new BackupArchiveError('This file is not a Flight Data Viewer backup.');
  }

  return {
    manifest: manifestRaw as BackupManifest,
    data,
    read: (paths) =>
      paths.size === 0 ? {} : unzipSync(bytes, { filter: (file) => paths.has(file.name) }),
  };
}

/** The manifest alone, without merging anything — the sync panel's list of devices. */
export function readArchiveManifest(bytes: Uint8Array): BackupManifest {
  return openArchive(bytes).manifest;
}

/**
 * How a merge treats what this device already holds.
 *
 * - `'restore'` — the pilot chose a backup and asked for it back. Deletions
 *   recorded here are overridden: a flight deleted by mistake is exactly what
 *   a restore is for. Deletions recorded in the archive are ignored.
 * - `'sync'` — another device's file, read unasked. Deletions travel in both
 *   directions, newest wins, so a flight deleted on the Mac does not come
 *   back from the iPhone's older copy.
 *
 * Both are newest-wins for an edited record and never touch a record the
 * archive holds an older copy of. See `docs/SYNC.md`.
 */
export type MergeMode = 'restore' | 'sync';

interface Stamped {
  updatedAt: string;
}

/** Newer by `updatedAt`. An archive's copy wins only when strictly newer, so a merge of the same file twice changes nothing. */
function isNewer(incoming: Stamped, local: Stamped): boolean {
  return incoming.updatedAt > local.updatedAt;
}

function remap(map: ReadonlyMap<string, string>, id: string): string {
  return map.get(id) ?? id;
}

/**
 * Merge an archive into the local database.
 *
 * Tracks are immutable and matched by id and then by content hash, the same
 * duplicate check a normal import makes. Every other record is matched by id
 * and newest-wins by `updatedAt`. Two devices that each created the same
 * thing independently — ZK-DFH added on both, or one FR24 file imported on
 * both — are matched by what makes them the same (registration; the track a
 * flight is linked to; the flight an entry is for), and every reference to
 * the archive's copy is pointed at the one already here instead of creating
 * a second.
 */
export async function mergeArchive(
  bytes: Uint8Array,
  { mode }: { mode: MergeMode },
): Promise<RestoreSummary> {
  const archive = openArchive(bytes);
  const { data } = archive;
  const summary = emptySummary();
  const now = new Date().toISOString();

  /* Deletions first, so nothing below re-adds what the archive's device removed. */
  if (mode === 'sync') {
    for (const tombstone of data.tombstones ?? []) {
      const deleted = await applyTombstone(tombstone);
      if (deleted) {
        summary.recordsDeleted += 1;
      }
    }
  }
  const localTombstones = new Map(
    (await tombstonesRepo.list()).map((row) => [row.key, row.deletedAt]),
  );
  const tombstonedAt = (table: TombstoneTable, id: string): string | undefined =>
    localTombstones.get(`${table}:${id}`);
  /** In a sync, a record deleted here after the archive's copy was written stays deleted. */
  const staysDeleted = (table: TombstoneTable, id: string, stamp: string): boolean => {
    const deletedAt = tombstonedAt(table, id);
    return mode === 'sync' && deletedAt !== undefined && deletedAt >= stamp;
  };
  /**
   * In a restore, a record coming back over its own deletion is stamped now,
   * so the other devices' tombstones for it are older than it is and it
   * survives the next sync rather than being deleted again.
   */
  const revived = <T extends Stamped>(table: TombstoneTable, id: string, record: T): T =>
    tombstonedAt(table, id) === undefined ? record : { ...record, updatedAt: now };

  /*
   * Where each archived record lives in *this* database. Usually the same id,
   * but a record skipped because this database already holds the same thing
   * under another id is still referred to by the archive's other records.
   */
  const trackIds = new Map<string, string>();
  const aircraftIds = new Map<string, string>();
  const flightIds = new Map<string, string>();

  const toAdd: Track[] = [];
  for (const track of data.tracks) {
    if ((await tracksRepo.get(track.id)) !== undefined) {
      trackIds.set(track.id, track.id);
      summary.tracksSkipped += 1;
      continue;
    }
    if (staysDeleted('tracks', track.id, track.importedAt)) {
      summary.tracksSkipped += 1;
      continue;
    }
    const existing = await tracksRepo.findByContentHash(track.contentHash);
    if (existing !== undefined) {
      trackIds.set(track.id, existing.id);
      summary.tracksSkipped += 1;
      continue;
    }
    toAdd.push(track);
  }

  const pointsFor = loadPoints(archive, toAdd);
  for (const track of toAdd) {
    const points = pointsFor.get(track.id);
    if (points === undefined) {
      summary.tracksSkipped += 1;
      continue;
    }
    const importedAt = tombstonedAt('tracks', track.id) === undefined ? track.importedAt : now;
    await tracksRepo.add({ ...track, importedAt }, points);
    trackIds.set(track.id, track.id);
    summary.tracksAdded += 1;
  }

  const trackFileMeta = data.trackFiles ?? [];
  const trackFileBytes = archive.read(new Set(trackFileMeta.map(trackFilePath)));
  for (const meta of trackFileMeta) {
    const trackId = trackIds.get(meta.trackId);
    const fileBytes = trackFileBytes[trackFilePath(meta)];
    if (trackId === undefined || fileBytes === undefined) {
      summary.trackFilesSkipped += 1;
      continue;
    }
    const stored = await trackFilesRepo.attach(
      encodeTrackFile({
        id: (await trackFilesRepo.get(meta.id)) === undefined ? meta.id : crypto.randomUUID(),
        trackId,
        filename: meta.filename,
        bytes: fileBytes,
        addedAt: meta.addedAt,
      }),
    );
    if (stored) {
      summary.trackFilesAdded += 1;
    } else {
      summary.trackFilesSkipped += 1;
    }
  }

  for (const record of data.aircraft) {
    /*
     * A backup is a file, and a file can be older than the schema restoring
     * it. These rows go straight into the table, *after* whatever migration
     * this database has already run — so an archive written before aircraft
     * carried the register's category would otherwise restore records that
     * nothing ever converts. Same conversion the migration uses, for that
     * reason.
     */
    const upgradedRaw = { ...record } as unknown as Record<string, unknown>;
    upgradeAircraftCategory(upgradedRaw);
    const upgraded = upgradedRaw as unknown as Aircraft;

    const local = await aircraftRepo.get(upgraded.id);
    if (local !== undefined) {
      aircraftIds.set(upgraded.id, upgraded.id);
      if (isNewer(upgraded, local) && (await putUnlessRegistrationTaken(upgraded))) {
        summary.recordsUpdated += 1;
      } else {
        summary.aircraftSkipped += 1;
      }
      continue;
    }
    const sameRegistration = await aircraftRepo.findByRegistration(upgraded.registration);
    if (sameRegistration !== undefined) {
      aircraftIds.set(upgraded.id, sameRegistration.id);
      summary.aircraftSkipped += 1;
      continue;
    }
    if (staysDeleted('aircraft', upgraded.id, upgraded.updatedAt)) {
      summary.aircraftSkipped += 1;
      continue;
    }
    await aircraftRepo.put(revived('aircraft', upgraded.id, upgraded));
    aircraftIds.set(upgraded.id, upgraded.id);
    summary.aircraftAdded += 1;
  }

  for (const archived of data.flights) {
    const flight: Flight = {
      ...archived,
      ...(archived.trackId === undefined ? {} : { trackId: remap(trackIds, archived.trackId) }),
      ...(archived.aircraftId === undefined
        ? {}
        : { aircraftId: remap(aircraftIds, archived.aircraftId) }),
    };
    const local = await flightsRepo.get(flight.id);
    if (local !== undefined) {
      flightIds.set(flight.id, flight.id);
      if (isNewer(flight, local)) {
        await flightsRepo.put(flight);
        summary.recordsUpdated += 1;
      } else {
        summary.flightsSkipped += 1;
      }
      continue;
    }
    const sameTrack =
      flight.trackId === undefined ? undefined : await flightsRepo.findByTrack(flight.trackId);
    if (sameTrack !== undefined) {
      flightIds.set(flight.id, sameTrack.id);
      summary.flightsSkipped += 1;
      continue;
    }
    if (staysDeleted('flights', flight.id, flight.updatedAt)) {
      summary.flightsSkipped += 1;
      continue;
    }
    await flightsRepo.put(revived('flights', flight.id, flight));
    flightIds.set(flight.id, flight.id);
    summary.flightsAdded += 1;
  }

  for (const archived of data.logbookEntries ?? []) {
    const entry: LogbookEntry = {
      ...archived,
      flightId: remap(flightIds, archived.flightId),
      ...(archived.aircraftId === undefined
        ? {}
        : { aircraftId: remap(aircraftIds, archived.aircraftId) }),
    };
    const local = await logbookEntriesRepo.get(entry.id);
    if (local !== undefined) {
      if (isNewer(entry, local)) {
        await logbookEntriesRepo.put(entry);
        summary.recordsUpdated += 1;
      } else {
        summary.logbookEntriesSkipped += 1;
      }
      continue;
    }
    const sameFlight = await logbookEntriesRepo.findByFlight(entry.flightId);
    if (sameFlight !== undefined || staysDeleted('logbookEntries', entry.id, entry.updatedAt)) {
      summary.logbookEntriesSkipped += 1;
      continue;
    }
    await logbookEntriesRepo.put(revived('logbookEntries', entry.id, entry));
    summary.logbookEntriesAdded += 1;
  }

  for (const archived of data.logbookChecks ?? []) {
    const check: LogbookCheck = {
      ...archived,
      ...(archived.flightId === undefined ? {} : { flightId: remap(flightIds, archived.flightId) }),
    };
    const local = await logbookChecksRepo.get(check.id);
    if (local !== undefined) {
      if (isNewer(check, local)) {
        await logbookChecksRepo.put(check);
        summary.recordsUpdated += 1;
      } else {
        summary.logbookChecksSkipped += 1;
      }
      continue;
    }
    if (staysDeleted('logbookChecks', check.id, check.updatedAt)) {
      summary.logbookChecksSkipped += 1;
      continue;
    }
    await logbookChecksRepo.put(revived('logbookChecks', check.id, check));
    summary.logbookChecksAdded += 1;
  }

  const currentSettings = new Map((await settingsRepo.listRows()).map((row) => [row.key, row]));
  for (const setting of data.settings) {
    if (!isPortableSetting(setting.key)) {
      continue;
    }
    const local = currentSettings.get(setting.key);
    if (local === undefined) {
      await settingsRepo.putRow(setting);
      summary.settingsAdded += 1;
    } else if (typeof setting.updatedAt === 'string' && isNewer(setting, local)) {
      await settingsRepo.putRow(setting);
      summary.recordsUpdated += 1;
    } else {
      summary.settingsSkipped += 1;
    }
  }

  return summary;
}

/** Restores a backup the pilot chose. See `MergeMode`. */
export async function restoreBackupArchive(bytes: Uint8Array): Promise<RestoreSummary> {
  return mergeArchive(bytes, { mode: 'restore' });
}

function loadPoints(archive: OpenedArchive, tracks: readonly Track[]): Map<string, TrackPoints> {
  const wanted = new Set(tracks.map((track) => track.id));
  const result = new Map<string, TrackPoints>();

  for (const legacy of archive.data.trackPoints ?? []) {
    if (wanted.has(legacy.trackId)) {
      result.set(legacy.trackId, deserialisePoints(legacy));
    }
  }

  const headers = (archive.data.points ?? []).filter((header) => wanted.has(header.trackId));
  const files = archive.read(new Set(headers.map((header) => pointsPath(header.trackId))));
  for (const header of headers) {
    const bytes = files[pointsPath(header.trackId)];
    if (bytes === undefined) {
      continue;
    }
    try {
      result.set(header.trackId, decodePoints(header, bytes));
    } catch {
      // A damaged points file loses that one track, which is then counted as
      // skipped; it does not lose the rest of the archive.
    }
  }
  return result;
}

async function putUnlessRegistrationTaken(record: Aircraft): Promise<boolean> {
  const holder = await aircraftRepo.findByRegistration(record.registration);
  if (holder !== undefined && holder.id !== record.id) {
    // Renamed on the other device to a registration another aircraft here
    // already holds. The unique index would refuse it; keeping the local
    // copy is the one answer that loses nothing.
    return false;
  }
  await aircraftRepo.put(record);
  return true;
}

/**
 * Applies one deletion from another device.
 *
 * @returns whether a record here was deleted.
 */
async function applyTombstone(tombstone: Tombstone): Promise<boolean> {
  const { table, id, deletedAt } = tombstone;
  const localStamp = await stampOf(table, id);
  if (localStamp === undefined) {
    // Nothing here to delete, but remember it, so an older copy arriving from
    // a third device later is recognised as deleted too.
    await tombstonesRepo.record(table, id, deletedAt);
    return false;
  }
  if (localStamp > deletedAt) {
    // Edited here after it was deleted there. The edit is newer, so it stands.
    return false;
  }
  switch (table) {
    case 'tracks':
      await tracksRepo.delete(id, deletedAt);
      break;
    case 'flights':
      await flightsRepo.delete(id, deletedAt);
      break;
    case 'aircraft':
      await aircraftRepo.delete(id, deletedAt);
      break;
    case 'logbookEntries':
      await logbookEntriesRepo.delete(id, deletedAt);
      break;
    case 'logbookChecks':
      await logbookChecksRepo.delete(id, deletedAt);
      break;
  }
  return true;
}

async function stampOf(table: TombstoneTable, id: string): Promise<string | undefined> {
  switch (table) {
    case 'tracks':
      return (await tracksRepo.get(id))?.importedAt;
    case 'flights':
      return (await flightsRepo.get(id))?.updatedAt;
    case 'aircraft':
      return (await aircraftRepo.get(id))?.updatedAt;
    case 'logbookEntries':
      return (await logbookEntriesRepo.get(id))?.updatedAt;
    case 'logbookChecks':
      return (await logbookChecksRepo.get(id))?.updatedAt;
  }
}

/**
 * Every track as a file, in one zip — for a pilot who wants their flights
 * out of this app and into a folder, not a backup of it.
 *
 * Each track's originals are included as they were imported. A track with no
 * original (fetched from a network source, or imported before originals were
 * kept) is written out as GPX instead, so every flight is in the zip in some
 * form a map tool can open. Grouped by UTC date so the folder sorts the way a
 * logbook reads.
 *
 * @returns `null` when there are no tracks at all, so the caller can say so
 *   rather than hand over an empty zip.
 */
export async function buildTrackFilesArchive(): Promise<Blob | null> {
  const [tracks, trackFiles] = await Promise.all([db.tracks.toArray(), trackFilesRepo.list()]);
  if (tracks.length === 0) {
    return null;
  }

  const filesByTrack = new Map<string, TrackFile[]>();
  for (const file of trackFiles) {
    filesByTrack.set(file.trackId, [...(filesByTrack.get(file.trackId) ?? []), file]);
  }

  const entries: Record<string, Uint8Array> = {};
  const place = (folder: string, filename: string, bytes: Uint8Array): void => {
    const base = filename.split(/[\\/]/).pop() ?? 'file';
    let path = `${folder}/${base}`;
    // Two FR24 exports of different days can share a name; the second gets a
    // suffix rather than silently replacing the first.
    for (let n = 2; path in entries; n += 1) {
      const dot = base.lastIndexOf('.');
      path =
        dot <= 0
          ? `${folder}/${base} (${String(n)})`
          : `${folder}/${base.slice(0, dot)} (${String(n)})${base.slice(dot)}`;
    }
    entries[path] = bytes;
  };

  for (const track of tracks) {
    const folder = track.startTime.slice(0, 10) || 'undated';
    const originals = filesByTrack.get(track.id) ?? [];
    if (originals.length > 0) {
      for (const file of originals) {
        place(folder, file.filename, decodeTrackFile(file));
      }
      continue;
    }
    const points = await tracksRepo.getPoints(track.id);
    if (points === undefined) {
      continue;
    }
    const exported = exportTrack(track, points, 'gpx');
    place(folder, exported.filename, new TextEncoder().encode(exported.text));
  }

  return new Blob([zipSync(entries, { level: 6 })], { type: 'application/zip' });
}
